/**
 * BackendStatusContext - Global backend health tracking
 * 
 * Tracks HTTP 5xx errors across the app and surfaces a modal when
 * the backend is intermittently unavailable (502, 520, etc.)
 * 
 * This solves the post-ingestion navigation issue where Gate 7 succeeds
 * but the UI can't render because /api/auth/me returns 502.
 */
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const BackendStatusContext = createContext(null);

// Configuration
const CONFIG = {
  // Number of consecutive 5xx errors before showing modal
  errorThreshold: 2,
  // Time window to count errors (ms)
  errorWindowMs: 30000,
  // Time to wait before auto-hiding modal after recovery
  recoveryDelayMs: 2000,
  // Codes that indicate backend unavailable (not app logic errors)
  backendUnavailableCodes: [502, 503, 504, 520, 521, 522, 523, 524, 0],
};

export const BackendStatusProvider = ({ children }) => {
  const [isBackendUnavailable, setIsBackendUnavailable] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [errorCount, setErrorCount] = useState(0);
  const [isRecovering, setIsRecovering] = useState(false);
  
  // Track errors with timestamps for windowed counting
  const errorLog = useRef([]);
  const recoveryTimeout = useRef(null);
  
  // Clean old errors outside the window
  const cleanOldErrors = useCallback(() => {
    const now = Date.now();
    errorLog.current = errorLog.current.filter(
      ts => now - ts < CONFIG.errorWindowMs
    );
  }, []);
  
  // Report a backend error (called from API layer)
  const reportBackendError = useCallback((statusCode, url, errorMessage) => {
    // Only track backend unavailable errors, not 4xx or app errors
    if (!CONFIG.backendUnavailableCodes.includes(statusCode)) {
      return;
    }
    
    console.warn(`[BackendStatus] Backend error: ${statusCode} on ${url}`);
    
    // Add to error log
    errorLog.current.push(Date.now());
    cleanOldErrors();
    
    const currentCount = errorLog.current.length;
    setErrorCount(currentCount);
    setLastError({ statusCode, url, message: errorMessage, timestamp: Date.now() });
    
    // Show modal if threshold exceeded
    if (currentCount >= CONFIG.errorThreshold && !isBackendUnavailable) {
      console.warn(`[BackendStatus] Backend unavailable - showing modal (${currentCount} errors in window)`);
      setIsBackendUnavailable(true);
      setIsRecovering(false);
      
      // Clear any pending recovery timeout
      if (recoveryTimeout.current) {
        clearTimeout(recoveryTimeout.current);
        recoveryTimeout.current = null;
      }
    }
  }, [cleanOldErrors, isBackendUnavailable]);
  
  // Report a successful backend response (resets error tracking)
  const reportBackendSuccess = useCallback(() => {
    if (errorLog.current.length > 0 || isBackendUnavailable) {
      console.log('[BackendStatus] Backend recovered - successful response');
      errorLog.current = [];
      setErrorCount(0);
      
      if (isBackendUnavailable) {
        setIsRecovering(true);
        
        // Auto-hide modal after recovery delay
        recoveryTimeout.current = setTimeout(() => {
          setIsBackendUnavailable(false);
          setIsRecovering(false);
          setLastError(null);
        }, CONFIG.recoveryDelayMs);
      }
    }
  }, [isBackendUnavailable]);
  
  // Manual dismiss (user clicks "Try Again" or closes modal)
  const dismissModal = useCallback(() => {
    setIsBackendUnavailable(false);
    setIsRecovering(false);
    // Don't clear error log - if errors continue, modal will reappear
  }, []);
  
  // Manual retry - clears error log and dismisses
  const retryAndDismiss = useCallback(() => {
    errorLog.current = [];
    setErrorCount(0);
    setLastError(null);
    setIsBackendUnavailable(false);
    setIsRecovering(false);
    
    // Trigger a page reload to retry all requests
    window.location.reload();
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recoveryTimeout.current) {
        clearTimeout(recoveryTimeout.current);
      }
    };
  }, []);
  
  const value = {
    isBackendUnavailable,
    isRecovering,
    lastError,
    errorCount,
    reportBackendError,
    reportBackendSuccess,
    dismissModal,
    retryAndDismiss,
  };
  
  return (
    <BackendStatusContext.Provider value={value}>
      {children}
    </BackendStatusContext.Provider>
  );
};

export const useBackendStatus = () => {
  const context = useContext(BackendStatusContext);
  if (!context) {
    throw new Error('useBackendStatus must be used within BackendStatusProvider');
  }
  return context;
};

// Export for use in API layer (can be called even outside React components)
let globalReportError = null;
let globalReportSuccess = null;

export const setGlobalBackendStatusReporter = (reportError, reportSuccess) => {
  globalReportError = reportError;
  globalReportSuccess = reportSuccess;
};

export const reportGlobalBackendError = (statusCode, url, message) => {
  if (globalReportError) {
    globalReportError(statusCode, url, message);
  }
};

export const reportGlobalBackendSuccess = () => {
  if (globalReportSuccess) {
    globalReportSuccess();
  }
};
